/* ==========================================================================
   ZoeCrafts — global.js
   Shared helpers used across custom elements. Loaded with `defer` from
   layout/theme.liquid, so the DOM is ready by the time this evaluates.
   Money is ALWAYS server-rendered (| money); nothing here formats currency.
   ========================================================================== */

/** Standard fetch config for Shopify Ajax cart endpoints. */
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/** Pull the inner HTML of `selector` out of a returned Section Rendering string. */
function getSectionInnerHTML(html, selector = ".shopify-section") {
  const node = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  return node ? node.innerHTML : "";
}

/* ---- focus trap (drawer, mobile nav, any modal surface) ------------------- */
const trapFocusHandlers = {};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-'])," +
        "area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  const els = getFocusableElements(container);
  const first = els[0];
  const last = els[els.length - 1];
  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () => document.removeEventListener("keydown", trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = (event) => {
    if (event.code?.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first?.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last?.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  (elementToFocus || container).focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

/** True when the visitor asked for reduced motion. */
function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/* ---- <details> disclosure: close on outside click / Escape ---------------- */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.details?.querySelector("summary + *");
    this.details?.addEventListener("toggle", () => this.onToggle());
  }

  onToggle() {
    if (!this.details.open) return;
    this.onBodyClick = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener("click", this.onBodyClick);
    document.addEventListener("keyup", (e) => e.code === "Escape" && this.close());
  }

  close() {
    if (this.details) this.details.open = false;
    document.removeEventListener("click", this.onBodyClick);
  }
}
if (!customElements.get("details-disclosure")) customElements.define("details-disclosure", DetailsDisclosure);
